import React, { createContext, useContext } from 'react';
import useFetchData from '../../hooks/useFetchData';
import ConnectriaTicketsService from '../../services/ConnectriaTicketsService';
import YetiService from '../../services/YetiServices';
import YetiTicketProvider from '../../services/YetiTickets';
import { PROVIDERS } from './constants';

const formatTicketError = (error, message) => {
  if (!error) return null;

  const sectionError = new Error(message);
  sectionError.response = error.response;
  sectionError.cause = error;
  return sectionError;
};

const fetchAttachments = async (provider, ticketId, cancelToken) => {
  try {
    switch (provider) {
      case PROVIDERS.CONNECTRIA:
        return await ConnectriaTicketsService.ticketAttachments(ticketId);

      case PROVIDERS.LIGHTEDGE: {
        const attachmentsResponse = await YetiTicketProvider.getTicketAttachments(ticketId, { cancelToken });

        if (attachmentsResponse.data.code === 200) {
          return attachmentsResponse?.data?.results?.map((attachment) =>
              YetiTicketProvider.parseTicketAttachment(attachment, ticketId)
          );
        } else throw new Error("Unable to retrieve attachments. Please try again.");
      }
      default:
        throw new Error(`Unknown provider (${provider}) for ticket (${ticketId})`);
    }
  } catch (error) {
    throw error;
  }
};

const fetchComments = async (provider, ticketId, cancelToken) => {
  try {
    switch (provider) {
      case PROVIDERS.CONNECTRIA: {
        const [history, ratings] = await Promise.all([
          ConnectriaTicketsService.ticketHistory(ticketId, { cancelToken }),
          ConnectriaTicketsService.getTicketCommentRatings(ticketId, { cancelToken }),
        ]);
        const comments = (history || []).filter((comment) => !comment.pvt);
        return { comments, ratings };
      }

      case PROVIDERS.LIGHTEDGE: {
        let parsedComments;
        const commentsResponse = await YetiTicketProvider.getTicketComments(ticketId, { cancelToken });

        if (commentsResponse.data.code === 200) {
          parsedComments = commentsResponse?.data?.results?.map((comment) => YetiTicketProvider.parseTicketComment(comment)) || [];
        } else throw new Error("Unable to retrieve comments. Please try again.");

        return {
          comments: parsedComments,
          ratings: []
        };
      }

      default:
        throw new Error(`Unknown provider (${provider}) for ticket (${ticketId})`);
    }
  } catch (error) {
    throw error;
  }
};

const fetchTicketDetails = async (provider, ticketId, cancelToken) => {
  try {
    switch (provider) {
      case PROVIDERS.CONNECTRIA:
        return ConnectriaTicketsService.ticketDetail(ticketId, { cancelToken });

      case PROVIDERS.LIGHTEDGE: {
        let ticket;
        let recipients;
        let service;
        let services;
        let recipientsError = null;
        let serviceListError = null;

        const [ticketResponse, recipientsResponse, servicesResponse] = await Promise.all([
          YetiTicketProvider.getTicket(ticketId, { cancelToken }),
          YetiTicketProvider.getTicketRecipients(ticketId).catch(() => null),
          YetiService.serviceList().catch(() => null),
        ]);

        if (ticketResponse?.data?.code === 200) {
          ticket = YetiTicketProvider.parseTicket(ticketResponse.data.results);
        } else throw new Error("Unable to retrieve ticket. Please try again.");

        if (recipientsResponse?.data?.code === 200) {
          recipients = recipientsResponse.data.results || [];
        } else {
          recipientsError = "Unable to retrieve recipients. Please try again.";
        }

        if (servicesResponse?.data?.code === 200) {
          services = servicesResponse.data.results || [];
          service = services.find((service) => service?.id === ticket?.device_id);
        } else {
          serviceListError = "Unable to retrieve service data. Please try again.";
        }

        return { ...ticket, recipients, service, recipientsError, serviceListError };
      }

      default:
        return {};
    }
  } catch (error) {
    console.error('Error fetching ticket details:', error);
    throw error;
  }
};


const TicketDataContext = createContext();
export const useTicketData = () => useContext(TicketDataContext);

export const TicketDataProvider = ({ ticketId, provider, children }) => {
  const {
    data: attachments = [],
    isLoading: isLoadingAttachments,
    error: attachmentsError,
    revalidate: revalidateAttachments,
  } = useFetchData(
    (cancelToken) => fetchAttachments(provider, ticketId, cancelToken),
    [provider, ticketId]
  );

  const {
    data: commentData = { comments: [], ratings: [] },
    isLoading: isLoadingComments,
    error: commentsError,
    revalidate: revalidateComments,
  } = useFetchData(
    (cancelToken) => fetchComments(provider, ticketId, cancelToken),
    [provider, ticketId]
  );

  const {
    data: ticketDetails = {},
    isLoading: isLoadingTicketDetails,
    error: ticketDetailsErrors,
    revalidate: revalidateTicketDetails,
  } = useFetchData(
    (cancelToken) => fetchTicketDetails(provider, ticketId, cancelToken),
    [provider, ticketId]
  );

  const comments = commentData?.comments || [];
  const ratings = commentData?.ratings || [];

  return (
    <TicketDataContext.Provider
      value={{
        ticketId,
        attachments,
        attachmentsError: formatTicketError(attachmentsError, 'Unable to retrieve ticket attachments. Please try again.'),
        comments,
        commentsError: formatTicketError(commentsError, 'Unable to retrieve ticket comments. Please try again.'),
        isLoadingAttachments,
        isLoadingComments,
        isLoadingTicketDetails,
        provider,
        ratings,
        revalidateAttachments,
        revalidateComments,
        revalidateTicketDetails,
        ticketDetails,
        ticketDetailsErrors: formatTicketError(ticketDetailsErrors, 'Unable to retrieve ticket details. Please try again.'),
      }}
    >
      {children}
    </TicketDataContext.Provider>
  );
};
